/* eslint-disable @typescript-eslint/no-explicit-any */
import { parseLocaleNumber, transformPercent, transformPriceForChartTooltip } from 'src/util/functions';
import dayjs from 'dayjs';
import { IChartOptions, IChartTooltip, IMoneyPoint } from 'src/models/charts.model';
import { ECurrencyCode } from 'src/models/common.model';
import { ApexStroke, ApexFill, ApexAxisChartSeries } from 'ng-apexcharts';

function ensureHistoryDevelopmentOverlay(el: HTMLElement): HTMLElement | null {
    const canvas = (el.classList.contains('apexcharts-canvas')
        ? el : el.querySelector('.apexcharts-canvas')) as HTMLElement | null;
    const container = canvas?.parentElement as HTMLElement | undefined;
    if (!canvas || !container) return null;

    let overlay = Array.from(container.children)
        .find(c => c.classList.contains('hd-chart-overlay')) as HTMLElement | undefined;
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'hd-chart-overlay';
        overlay.innerHTML =
            '<svg class="hd-chart-overlay__svg" preserveAspectRatio="none">' +
                '<line class="hd-chart-overlay__line" />' +
            '</svg>' +
            '<div class="hd-chart-overlay__box"></div>';
        if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
        container.appendChild(overlay);
    }
    return overlay;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function setupHistoryDevelopmentChartOverlay(chartContext: any): void {
    const root = chartContext.el as HTMLElement;
    ensureHistoryDevelopmentOverlay(root); // create eagerly so there's no first-hover flash

    // `mounted` can fire again on a full re-render — don't double-bind
    if ((root as any).__hdHideBound) return;
    (root as any).__hdHideBound = true;

    // resolve the *current* overlay each time; never hold a stale reference
    const hide = (): void =>
        root.querySelector('.hd-chart-overlay')?.classList.remove('is-visible');

    root.addEventListener('mouseleave', hide);                       // desktop: pointer leaves the chart
    root.addEventListener('touchend', hide, { passive: true });      // mobile: finger lifted
    root.addEventListener('touchcancel', hide, { passive: true });   // mobile: gesture interrupted
}

export const HISTORY_DEVELOPMENT_CHART_CONFIG: Partial<IChartOptions> = {
    series: [],
    chart: {
        animations: {
            enabled: false,
        },
        height: 196,
        type: 'area',
        zoom: {
            enabled: false,
        },
        toolbar: {
            show: false,
        },
        sparkline: {
            enabled: true,
        },
        events: {
            mounted: setupHistoryDevelopmentChartOverlay,
        },
    },
    dataLabels: {
        enabled: false,
    },
    grid: {
        row: {
            opacity: 0,
        },
        column: {
            opacity: 0,
        },
        padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0
        }
    },
    xaxis: {
        type: 'datetime',
        labels: {
            show: false,
        },
        crosshairs: { show: false }
    },
    yaxis: {
        labels: {
            show: false,
        },
    },
    legend: {
        show: false
    },
    markers: {
        size: [1, 5],
        colors: ['transparent'],
        strokeColors: '#3D8146',
        shape: 'square',
        strokeWidth: 0,
        strokeDashArray: 0,
        hover: {
            sizeOffset: 0,
        },
    },
    tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: (input) => {
            updateHistoryDevelopmentChartOverlay(input, {
                currencyOverride: ('CZK') as ECurrencyCode,
                toTwoDecimal: true,
                freezeAtEdge: true,
            });
            return '';   // ApexCharts own box stays hidden
        },
    },
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function handleTooltipPosition(event: any, chartContext: any): void {
    const chart = chartContext.el as HTMLElement;
    const tooltip = chart.querySelector('.apexcharts-tooltip') as HTMLElement | null;
    if (!tooltip) return;

    // touchend sentinel + leaving the plot → hide
    const clientX = event.clientX ?? event.touches?.[0]?.clientX ?? -1;
    const crosshair = chart.querySelector('.apexcharts-xcrosshairs') as SVGLineElement | null;
    const x1 = crosshair?.getAttribute('x1');
    const active = crosshair?.classList.contains('apexcharts-active');

    if (clientX < 0 || !active || x1 === null || !x1) {
        chart.classList.add('apexcharts-tooltip-hidden');
        return;
    }
    chart.classList.remove('apexcharts-tooltip-hidden');

    const g = chartContext.w.globals;
    const plotLeft = g.translateX;
    const plotRight = g.translateX + g.gridWidth;

    const dataX = parseFloat(x1);          // real crosshair / data-point x
    const w = tooltip.offsetWidth;
    const half = w / 2;

    // centered on the crosshair, clamped on-screen, but the edge may reach the
    // crosshair past the plot bound at the extremes (kills the sub-pixel poke)
    const minLeft = Math.min(plotLeft, dataX);
    const maxLeft = Math.max(plotRight - w, dataX - w);
    const left = Math.max(minLeft, Math.min(dataX - half, maxLeft));

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.right = 'auto';
    tooltip.style.transform = 'none';
}

interface IChartInput {
    series: number[][],
    seriesIndex: number,
    dataPointIndex: number,
    w: any, // context
};

export interface IChartTooltipConfig {
    currencyOverride?: ECurrencyCode,
    toTwoDecimal?: boolean,
    hideAmounts?: boolean,
}

// todo: https://gitlab.com/dfcz/bwm-frontend/-/issues/58
export function generateHistoryDevelopmentChartTooltip(input: IChartInput, config?: IChartTooltipConfig): string {
    const xValue = input.w.globals.seriesX[input.seriesIndex][input.dataPointIndex];
    const timestamp = dayjs(xValue).format('DD. MM. YYYY');
    const allValues = mapHistoryDevelopmentChartForTooltip(input.series, input.w, xValue);

    const hiddenClass = config?.hideAmounts ? 'financial-value--hidden' : '';
    const primaryValue = allValues[0].isPercent
        ? transformPercent(parseLocaleNumber(allValues[0].value + ''), true, 2)
        : `<span class="${hiddenClass}">${transformPriceForChartTooltip(allValues[0].value, config?.currencyOverride ?? allValues[0].currency, config?.toTwoDecimal)}</span>`;

    return `<div class="libero-history-development-chart-tooltip ${allValues[1]?.value ? 'libero-history-development-chart-tooltip--extended' : ''}">
                <div class="libero-history-development-chart-tooltip-name">
                    ${allValues[0]?.name}
                </div >
                <div class="libero-history-development-chart-tooltip-value">
                    ${primaryValue}
                </div>
                ${allValues[1]?.depositValue ?
                    ('<div class="libero-history-development-chart-tooltip-name">' +
                        allValues[1].name +
                    '</div>' +
                    '<div class="libero-history-development-chart-tooltip-value">' +
                        `<span class="${hiddenClass}">${transformPriceForChartTooltip(allValues[1].depositValue, config?.currencyOverride ?? allValues[1].currency)}</span>` +
                    '</div>'
                    ) :
                    ''
                }
            <div >
                ${timestamp}
            </div>
        </div>
    `
}

export function updateHistoryDevelopmentChartOverlay(
    input: IChartInput,
    config?: IChartTooltipConfig & { freezeAtEdge?: boolean },
): void {
    const g = input.w.globals;
    const baseEl = g.dom.baseEl as HTMLElement;
    const overlay = ensureHistoryDevelopmentOverlay(baseEl);   // your existing self-heal helper
    if (!overlay) return;

    // const line = overlay.querySelector('.hd-chart-overlay__line') as HTMLElement;
    const box = overlay.querySelector('.hd-chart-overlay__box') as HTMLElement;

    box.innerHTML = generateHistoryDevelopmentChartTooltip(input, config);

    const canvas = (baseEl.classList.contains('apexcharts-canvas')
        ? baseEl : baseEl.querySelector('.apexcharts-canvas')) as HTMLElement;
    const range = (g.maxX - g.minX) || 1;
    const xs: number[] = g.seriesX.reduce(
        (best: number[], s: number[]) => (s?.length ?? 0) > (best?.length ?? 0) ? s : best,
        g.seriesX[0],
    );
    const dataX = xs[input.dataPointIndex] ?? xs[xs.length - 1];
    const pixelX = ((dataX - g.minX) / range) * g.gridWidth;   // local to the plot, varies per point

    overlay.style.left = `${canvas.offsetLeft + g.translateX}px`;
    overlay.style.top = `${canvas.offsetTop + g.translateY}px`;
    overlay.style.width = `${g.gridWidth}px`;
    overlay.style.height = `${g.gridHeight}px`;

    const rect = overlay.getBoundingClientRect();
    const snap = (localX: number) => Math.round(rect.left + localX) - rect.left;

    const svg = overlay.querySelector('.hd-chart-overlay__svg') as SVGSVGElement;
    const line = overlay.querySelector('.hd-chart-overlay__line') as SVGLineElement;

    const STROKE = 2;
    const lineX = Math.max(STROKE / 2, Math.min(pixelX, g.gridWidth - STROKE / 2));

    svg.setAttribute('width', `${g.gridWidth}`);
    svg.setAttribute('height', `${g.gridHeight}`);
    line.setAttribute('x1', `${lineX}`);
    line.setAttribute('x2', `${lineX}`);
    line.setAttribute('y1', '0');
    line.setAttribute('y2', `${g.gridHeight}`);

    // BOX — centered on the point, clamped to the plot at the edges
    const bw = box.offsetWidth;
    const firstX = ((xs[0] - g.minX) / range) * g.gridWidth;              // first point
    const lastX  = ((xs[xs.length - 1] - g.minX) / range) * g.gridWidth;  // last point

    let boxLeft = pixelX - bw / 2;                        // centered on the crosshair
    if ((config?.freezeAtEdge ?? true) && lastX - bw > firstX) {
        boxLeft = Math.max(firstX, Math.min(boxLeft, lastX - bw));   // clamp to the data span
    }
    box.style.right = 'auto';
    box.style.left = `${snap(boxLeft)}px`;

    overlay.classList.add('is-visible');
}

// to avoid duplicit tooltip for deposits and display both values in single tooltip
// find both hovered periodic value and deposit (if there is any) at same(❗) timestamp
function mapHistoryDevelopmentChartForTooltip(series: number[][], ctx: any, xValue: number): IChartTooltip[] {
    const outputValues: IChartTooltip[] = [];
    series.forEach((serieData: number[], index: number) => {
        const seriesXValues = ctx.globals.seriesX[index];
        const matchingIndex = seriesXValues.findIndex((x: number) => x === xValue);
        if (matchingIndex !== -1) {
            const dataPoint = ctx.config.series?.[1]?.data?.[matchingIndex];
            const tooltipValueName = index === 1 ?
                dataPoint?.metadata?.depositTooltipName :
                ctx.globals.seriesNames[index];

            outputValues.push({
                name: tooltipValueName,
                value: serieData[matchingIndex],
                depositValue: dataPoint?.metadata?.depositValue ?? null,
                isPercent: ctx.config.series?.[0]?.data?.[index]?.metadata?.isPercent ?? null,
                currency: ctx.config.series?.[0]?.data?.[index]?.metadata?.currency ?? 'CZK',
            });
        }
    });
    return outputValues;
}

export function getChartStroke(color: string): ApexStroke {
    return {
        curve: ['straight', 'stepline'],
        width: 2,
        colors: [
            color,
            color,
        ],
    };
}

export function getChartFill(color: string): ApexFill {
    return {
        type: ['gradient'],
        gradient: {
            type: 'vertical',
            shadeIntensity: 1,
            opacityFrom: [1, 0],
            opacityTo: [0, 0],
            stops: [0, 100],
            colorStops: [
                {
                    offset: 0,
                    color: color,
                    opacity: .25
                },
                {
                    offset: 100,
                    color: color,
                    opacity: 0
                }
            ],
        }
    };
}

export function getDepositOrWithdrawValue(transactionX: number, transactionsInTime: IMoneyPoint[]): number {
    const correspondingDeposit = transactionsInTime.find(deposit => deposit.x === transactionX);
    if(correspondingDeposit) {
      if(correspondingDeposit.sumOfDeposits) {
        return correspondingDeposit.sumOfDeposits;
      } else if (correspondingDeposit.sumOfWithdrawals) {
        return -correspondingDeposit.sumOfWithdrawals;
      }
    }

    return NaN;
}

type SeriesDataType = ApexAxisChartSeries[number]['data'][number];
export function generateLastDeposit(series: ApexAxisChartSeries): SeriesDataType {
    const lastValue = series[0].data[series[0].data.length - 1] as { x: number };
    const lastDeposit = series[1].data[series[1].data.length - 1] as { y: number };

    return {
        x: lastValue?.x,
        y: lastDeposit?.y,
        fillColor: 'transparent',
    };
}
